import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import {
  HStack,
  IconButtonType,
  IconCTAButton,
  VStack,
} from "../../../../components/utils";
import {
  WorkflowRequirement,
  WorkflowRequirementKind,
  WorkflowStepInformation,
} from "../../../../types/Transaction";

type InformationRequest = {
  employeeId: string;
  eventId: string;
  requestId: string;
};

type Props = {
  employeeId: string;
  eventId: string;
  requestId: string;
  showRequestId?: boolean;
  contextLabel?: string;
  stepLabel: string;
  requirements: WorkflowRequirement[];
  loadInformation: (
    request: InformationRequest
  ) => Promise<WorkflowStepInformation>;
  onClose: () => void;
};

export default function WorkflowStepInformationDialog({
  employeeId,
  eventId,
  requestId,
  showRequestId = true,
  contextLabel,
  stepLabel,
  requirements,
  loadInformation,
  onClose,
}: Props) {
  const [information, setInformation] =
    useState<WorkflowStepInformation | null>(null);
  const [error, setError] = useState("");
  const orderedRequirements = [
    ...(information?.configSnapshot?.length
      ? information.configSnapshot
      : requirements),
  ].sort((a, b) => a.order - b.order);

  useEffect(() => {
    let active = true;
    setInformation(null);
    setError("");
    loadInformation({ employeeId, eventId, requestId })
      .then((response) => {
        if (active) setInformation(response);
      })
      .catch(() => {
        if (active) setError("Could not load the submitted information.");
      });
    return () => {
      active = false;
    };
  }, [employeeId, eventId, requestId, loadInformation]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <VStack className="min-h-0 min-w-0 max-h-[calc(100vh-64px)] overflow-hidden">
        <HStack className="w-full shrink-0 items-start justify-between gap-4 border-b border-borderColor px-6 py-4">
          <VStack className="min-w-0 gap-1">
            <h2 className="text-base leading-6 font-semibold text-headerColor">
              {stepLabel} information
            </h2>
            {(contextLabel || showRequestId) && (
              <p className="text-xs2 text-secondaryText">
                {[contextLabel, showRequestId ? `Request ${requestId}` : null]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            )}
          </VStack>
          <IconCTAButton
            iconOnly
            buttonType={IconButtonType.NONE}
            iconName="material-symbols:close"
            aria-label="Close willingness information"
            className="shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-secondary"
            onClick={onClose}
          />
        </HStack>

        <VStack className="min-h-0 min-w-0 overflow-y-auto overflow-x-hidden px-6 py-2">
          {error && <p className="py-6 text-sm text-red-600">{error}</p>}
          {!error && !information && (
            <p className="py-6 text-sm text-secondaryText">Loading…</p>
          )}
          {information &&
            orderedRequirements.map((item, index) => {
              if (item.kind === WorkflowRequirementKind.DECLARATION) {
                const declaration = information.declarations?.[item.id];
                return (
                  <HStack
                    key={item.id}
                    className="min-w-0 items-start gap-2 pb-4"
                  >
                    <Icon
                      icon={
                        declaration?.accepted
                          ? "material-symbols:check-box-rounded"
                          : "material-symbols:check-box-outline-blank"
                      }
                      aria-hidden="true"
                      className="mt-0.5 shrink-0 text-primaryText"
                      width={18}
                    />
                    <VStack className="min-w-0 gap-1">
                      <p className="whitespace-pre-wrap break-words text-sm leading-5 text-primaryText">
                        {declaration?.text ?? item.declaration?.text}
                      </p>
                      <p className="text-xs text-secondaryText">
                        {declaration?.accepted ? "Accepted" : "Not accepted"}
                        {declaration?.acceptedAt &&
                          ` on ${new Date(
                            declaration.acceptedAt
                          ).toLocaleString()}`}
                      </p>
                    </VStack>
                  </HStack>
                );
              }

              const documents = information.documents.filter(
                (document) => document.requirementId === item.id
              );
              return (
                <section
                  key={item.id}
                  className={`grid min-w-0 grid-cols-1 gap-2 py-4 sm:grid-cols-2 sm:gap-6 ${
                    index > 0 ? "border-t border-borderColor" : ""
                  }`}
                >
                  <h3 className="min-w-0 break-words text-xs2 font-semibold text-headerColor">
                    {item.label}
                  </h3>
                  {item.kind === WorkflowRequirementKind.DOCUMENT ? (
                    <VStack className="min-w-0 gap-2">
                      {documents.length ? (
                        documents.map((document) => (
                          <a
                            key={document.id}
                            href={document.url}
                            target="_blank"
                            rel="noreferrer"
                            title={document.name}
                            className="flex min-w-0 max-w-full items-center gap-2 rounded-lg border border-borderColor px-3 py-2 text-xs2 font-medium text-secondary hover:bg-slate-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-secondary"
                          >
                            <Icon
                              icon="material-symbols:description-outline"
                              aria-hidden="true"
                              className="shrink-0"
                              width={18}
                            />
                            <span className="min-w-0 flex-1 truncate">
                              {document.name}
                            </span>
                            <Icon
                              icon="material-symbols:open-in-new"
                              aria-hidden="true"
                              className="shrink-0"
                              width={16}
                            />
                          </a>
                        ))
                      ) : (
                        <p className="text-sm text-secondaryText">
                          Not uploaded
                        </p>
                      )}
                    </VStack>
                  ) : (
                    <p className="min-w-0 whitespace-pre-wrap break-words text-sm leading-5 font-medium text-primaryText">
                      {item.kind === WorkflowRequirementKind.QUESTION
                        ? item.question?.options.find(
                            (option) =>
                              option.id === information.choices?.[item.id]
                          )?.label ?? "Not answered"
                        : information.fields[item.id] || "Not provided"}
                    </p>
                  )}
                </section>
              );
            })}
          {information && orderedRequirements.length === 0 && (
            <p className="py-6 text-sm text-secondaryText">
              No additional information was configured.
            </p>
          )}
        </VStack>
      </VStack>
    </Dialog>
  );
}
